import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Download, PlayCircle } from 'lucide-react';
import { Container, Reveal, SectionLabel } from './primitives';
import { MARKETPLACE_URL } from './constants';
import useGoogleAnalytics from '../../hooks/useGoogleAnalytics';

const APPS = [
  {
    id: 0,
    logo: '/logotickettimes.png',
    demoPath: '/demo-ticket-times',
    installUrl:
      'https://www.zendesk.com/marketplace/apps/support/1096052/ticket-times/?queryID=63292b85f11c0fbc78dd4bc1623a089f',
  },
  {
    id: 1,
    logo: '/logoticketquality.png',
    demoPath: '/demo-ticket-quality',
    installUrl:
      'https://www.zendesk.com/marketplace/apps/support/1101694/ticket-quality/?queryID=75c171446c52aee83dd2d3fa92d3fde0',
  },
  {
    id: 2,
    logo: '/logoadminsuite.png',
    demoPath: '/demo-admin-suite',
    installUrl:
      'https://www.zendesk.com/marketplace/apps/support/1124202/admin-suite/?queryID=096a157d43c64bfac44721f42a5439ab',
  },
  {
    id: 3,
    logo: '/ticket_time_analyser.png',
    demoPath: '/demo-ticket-time-analyser',
    installUrl: 'https://www.zendesk.com.mx/marketplace/apps/support/1167820/ticket-time-analyser/',
  },
  {
    id: 4,
    logo: '/assigntogroup_image.png',
    demoPath: 'https://calendar.app.google/URR5NVNKa7FChwa76',
    installUrl: 'https://www.zendesk.com/marketplace/apps/support/1196892/assign-to-group/',
    isExternalDemo: true,
  },
];

export default function Marketplace() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { events } = useGoogleAnalytics();

  const appsCopy = t('video_uno_section.apps', { returnObjects: true });
  const copy = Array.isArray(appsCopy) ? appsCopy : [];

  const handleDemo = (app, name) => {
    if (app.isExternalDemo) {
      events.bookDemo(name);
      window.open(app.demoPath, '_blank', 'noopener,noreferrer');
    } else {
      events.watchDemo(name, 0);
      navigate(app.demoPath);
    }
  };

  return (
    <section id="marketplace" className="bg-surface py-20 sm:py-28">
      <Container>
        <Reveal className="max-w-2xl">
          <SectionLabel>{t('home.marketplace.eyebrow')}</SectionLabel>
          <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {t('home.marketplace.title')}
          </h2>
          <p className="mt-4 text-lg text-white/55">{t('home.marketplace.subtitle')}</p>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {APPS.map((app, i) => {
            const name = copy[app.id]?.name || '';
            const description = copy[app.id]?.description || '';
            return (
              <Reveal
                key={app.id}
                delay={(i % 3) * 70}
                className="flex h-full flex-col rounded-2xl border border-line bg-elevated p-6"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-white/10">
                  <img src={app.logo} alt={`${name} logo`} className="max-h-9 max-w-9 object-contain" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-white">{name}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-white/55 line-clamp-3">
                  {description}
                </p>
                <div className="mt-5 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleDemo(app, name)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-400 transition-colors hover:text-accent"
                  >
                    <PlayCircle className="h-4 w-4" />
                    {t('home.marketplace.demo')}
                  </button>
                  <a
                    href={app.installUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => events.installApp(name, app.installUrl)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-white/50 transition-colors hover:text-white"
                  >
                    <Download className="h-4 w-4" />
                    {t('home.marketplace.install')}
                  </a>
                </div>
              </Reveal>
            );
          })}

          {/* View all card */}
          <Reveal
            delay={140}
            className="flex h-full flex-col items-start justify-center rounded-2xl border border-dashed border-accent/30 bg-accent/[0.06] p-6"
          >
            <h3 className="text-lg font-semibold text-white">{t('home.marketplace.all_title')}</h3>
            <p className="mt-2 text-sm text-white/55">{t('home.marketplace.all_subtitle')}</p>
            <a
              href={MARKETPLACE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-400 hover:text-accent"
            >
              {t('home.marketplace.all_cta')}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
